import type {
  ConsultantDeadlineSameTypeSummaryResponse,
  DeadlineAssigneeCandidateDto,
  DeadlineValiditySuggestionDto,
} from '@prospero/shared';
import type {
  ConsultantDeadlineDigestStatusResponse,
  ConsultantDeadlineListResponse,
  ConsultantDeadlineSummaryResponse,
} from '@shared/dto/deadline/consultant-deadline.dto';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  ConsultantDeadlineListParams,
  ConsultantDeadlineSummaryParams,
  IConsultantDeadlinesService,
} from './consultant-deadlines.service.interface';

function toQueryString(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  }
  return query.toString();
}

@Injectable()
export class ConsultantDeadlinesService implements IConsultantDeadlinesService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async getSummary(
    params: ConsultantDeadlineSummaryParams,
  ): Promise<ConsultantDeadlineSummaryResponse> {
    const query = toQueryString({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search?.trim(),
      tenantId: params.tenantId,
      domain: params.domain,
      documentTypeId: params.documentTypeId,
      document: params.document?.trim(),
      bucket: params.bucket,
      recurrence: params.recurrence,
      dueFrom: params.dueFrom,
      dueTo: params.dueTo,
      archived: params.archived ? 'true' : undefined,
    });
    return this.http.get<ConsultantDeadlineSummaryResponse>(
      `consultant/deadlines/summary?${query}`,
    );
  }

  async listDeadlines(
    params: ConsultantDeadlineListParams,
  ): Promise<ConsultantDeadlineListResponse> {
    const query = toQueryString({
      page: params.page,
      pageSize: params.pageSize,
      tenantId: params.tenantId,
      domain: params.domain,
      documentTypeId: params.documentTypeId,
      document: params.document?.trim(),
      bucket: params.bucket,
      recurrence: params.recurrence,
      assigneeUserId: params.assigneeUserId,
      dueFrom: params.dueFrom,
      dueTo: params.dueTo,
      archived: params.archived ? 'true' : undefined,
    });
    return this.http.get<ConsultantDeadlineListResponse>(`consultant/deadlines?${query}`);
  }

  async getAssigneeCandidates(tenantId: string): Promise<DeadlineAssigneeCandidateDto[]> {
    const query = toQueryString({ tenantId });
    return this.http.get<DeadlineAssigneeCandidateDto[]>(
      `consultant/deadlines/assignee-candidates?${query}`,
    );
  }

  async getValiditySuggestion(documentTypeId: string): Promise<DeadlineValiditySuggestionDto> {
    const query = toQueryString({ documentTypeId });
    return this.http.get<DeadlineValiditySuggestionDto>(
      `consultant/deadlines/validity-suggestion?${query}`,
    );
  }

  async getSameTypeSummary(
    tenantIds: readonly string[],
    documentTypeId: string,
    assigneeUserId?: string,
  ): Promise<ConsultantDeadlineSameTypeSummaryResponse> {
    const query = toQueryString({
      tenantIds: tenantIds.join(','),
      documentTypeId,
      assigneeUserId,
    });
    return this.http.get<ConsultantDeadlineSameTypeSummaryResponse>(
      `consultant/deadlines/same-type-summary?${query}`,
    );
  }

  async getDigestStatus(): Promise<ConsultantDeadlineDigestStatusResponse> {
    return this.http.get<ConsultantDeadlineDigestStatusResponse>(
      'consultant/deadlines/digest-status',
    );
  }
}
